import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedDramas } from '@/mocks/dramas';
import type { Drama } from '@/mocks/dramas';

export default function HeroSection() {
  const featuredDramas = getFeaturedDramas();
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % featuredDramas.length);
    }, 5000);
  }, [featuredDramas.length]);

  useEffect(() => {
    startAutoPlay();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoPlay]);

  const goTo = (index: number) => {
    setCurrent(index);
    startAutoPlay();
  };

  const drama: Drama = featuredDramas[current] || featuredDramas[0];

  if (!drama) return null;

  return (
    <section className="relative w-full h-[500px] md:h-[650px] overflow-hidden">
      {featuredDramas.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.posterImage}
            alt={item.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background-50"></div>

      <div className="absolute inset-0 flex items-end md:items-center">
        <div className="w-full px-4 md:px-6 lg:px-8 max-w-[1280px] mx-auto pb-16 md:pb-0">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              {drama.isHot && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent-500 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                  正在热播
                </span>
              )}
              {drama.isNew && (
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-primary-500/90 text-background-50 dark:text-foreground-950 whitespace-nowrap">
                  最新上线
                </span>
              )}
              <span className="flex items-center gap-1 text-sm text-foreground-100">
                <i className="ri-star-fill text-primary-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                {drama.rating}
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-50 mb-3 leading-tight">
              {drama.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-300 mb-2 max-w-lg line-clamp-2">
              {drama.description}
            </p>
            <div className="flex items-center gap-3 text-xs md:text-sm text-foreground-400 mb-6">
              <span>{drama.genres.join(' / ')}</span>
              <span className="text-foreground-600">|</span>
              <span>{drama.year}</span>
              <span className="text-foreground-600">|</span>
              <span>{drama.episodes}集</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to={`/article/${drama.id}`}
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill mr-2"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${drama.category}`}
                className="px-6 py-3 rounded-lg bg-background-50/20 backdrop-blur-sm text-foreground-50 border border-foreground-50/20 text-sm md:text-base font-medium hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                更多{drama.region}剧集
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featuredDramas.map((item, index) => (
          <button
            key={item.id}
            onClick={() => goTo(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
              index === current
                ? 'w-6 bg-primary-500'
                : 'bg-foreground-50/40 hover:bg-foreground-50/60'
            }`}
            aria-label={`切换到 ${item.title}`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + featuredDramas.length) % featuredDramas.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-foreground-50 hover:bg-background-50/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % featuredDramas.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/20 backdrop-blur-sm text-foreground-50 hover:bg-background-50/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}